import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Mic, MicOff, Sparkles, Save, X, Trash2, Pencil, Loader2, Volume2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'initial_contact', label: 'Initial contact' },
  { value: 'in_discussion', label: 'In discussion' },
  { value: 'pledge_committed', label: 'Pledge committed' },
  { value: 'declined', label: 'Declined' },
  { value: 'on_hold', label: 'On hold' },
  { value: 'followup_needed', label: 'Follow up needed' },
];

const PLEDGE_TYPES = [
  { value: '', label: '—' },
  { value: 'prayer', label: 'Prayer' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'one_time', label: 'One-time' },
  { value: 'external', label: 'External' },
];

export const ConversationsBulkAI = ({ onComplete, onMessage }) => {
  const [open, setOpen] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [parsing, setParsing] = useState(false);
  const [entries, setEntries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [listening, setListening] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const [interimText, setInterimText] = useState('');
  const recognitionRef = useRef(null);

  useEffect(() => {
    const Sp = window.SpeechRecognition || window.webkitSpeechRecognition;
    setVoiceSupported(!!Sp);
  }, []);

  const startListening = () => {
    const Sp = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Sp) { onMessage && onMessage('Voice not supported in this browser. Try Chrome.'); return; }
    const rec = new Sp();
    rec.lang = navigator.language || 'en-US';
    rec.continuous = true;
    rec.interimResults = true;
    rec.onresult = (event) => {
      let finalPart = '';
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalPart += t + ' ';
        else interim += t;
      }
      if (finalPart) setTranscript((prev) => (prev ? prev + ' ' : '') + finalPart.trim());
      setInterimText(interim);
    };
    rec.onerror = (e) => {
      console.warn('voice error', e?.error);
      setListening(false);
      setInterimText('');
    };
    rec.onend = () => {
      setListening(false);
      setInterimText('');
    };
    rec.start();
    recognitionRef.current = rec;
    setListening(true);
  };

  const stopListening = () => {
    try { recognitionRef.current?.stop(); } catch (e) {
      // Web Speech API throws if recognition is already stopped — safe to ignore,
      // but log it so unexpected failures are still visible in DevTools.
      console.debug('SpeechRecognition.stop() failed:', e?.message);
    }
    setListening(false);
    setInterimText('');
  };

  const parseTranscript = async () => {
    if (!transcript.trim()) return;
    setParsing(true);
    try {
      const { data } = await axios.post(`${API}/admin/conversations/bulk-parse`,
        { transcript }, { withCredentials: true });
      // Add a stable React key for each parsed entry so remove/reorder
      // operations don't reuse DOM nodes for different rows.
      let nextId = Date.now();
      setEntries((data?.entries || []).map((e) => ({ ...e, _enabled: true, _key: `b${nextId++}` })));
      if (!data?.entries?.length) {
        onMessage && onMessage('The AI did not find any supporters in that text. Try giving more detail.');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Parse failed';
      onMessage && onMessage(`AI parse failed: ${detail}`);
    } finally {
      setParsing(false);
    }
  };

  const updateEntry = (idx, patch) => {
    setEntries((prev) => prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
  };

  const removeEntry = (idx) => {
    setEntries((prev) => prev.filter((_, i) => i !== idx));
  };

  const saveAll = async () => {
    const toSave = entries.filter((e) => e._enabled !== false && e.name?.trim());
    if (!toSave.length) {
      onMessage && onMessage('No entries to save.');
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/admin/conversations/bulk-save`,
        { entries: toSave.map(({ _enabled, _key, ...rest }) => rest) }, // strip internal-only fields
        { withCredentials: true });
      onMessage && onMessage(`Saved ${data?.created_count || 0} conversations`);
      setTranscript('');
      setEntries([]);
      setOpen(false);
      onComplete && onComplete();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Save failed';
      onMessage && onMessage(`Save failed: ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  const clearAll = () => {
    setTranscript('');
    setInterimText('');
    setEntries([]);
  };

  return (
    <div data-testid="conv-bulk-ai" className="bg-gradient-to-br from-purple-50 via-white to-blue-50 border border-purple-200 rounded-2xl p-6 shadow-sm">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center shadow">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-base font-bold text-slate-900 font-['Outfit']">AI Bulk Add</h3>
            <p className="text-xs text-slate-600">Dictate or paste notes about multiple supporters — AI extracts the details.</p>
          </div>
        </div>
        <button
          data-testid="conv-bulk-ai-toggle"
          onClick={() => setOpen((o) => !o)}
          className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
            open ? 'bg-slate-200 text-slate-700' : 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow hover:shadow-lg'
          }`}
        >
          {open ? 'Close' : 'Open AI tool'}
        </button>
      </div>

      {open && (
        <div className="space-y-4">
          {/* Input area */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Your notes</label>
              <div className="flex items-center gap-2">
                {voiceSupported && (
                  listening ? (
                    <button
                      data-testid="conv-bulk-ai-stop"
                      onClick={stopListening}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-full text-xs font-semibold shadow animate-pulse"
                    >
                      <MicOff className="w-3.5 h-3.5" /> Stop
                    </button>
                  ) : (
                    <button
                      data-testid="conv-bulk-ai-mic"
                      onClick={startListening}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 text-white rounded-full text-xs font-semibold shadow hover:bg-slate-800"
                    >
                      <Mic className="w-3.5 h-3.5" /> Speak
                    </button>
                  )
                )}
                {transcript && (
                  <button onClick={clearAll}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-slate-500 hover:text-red-600">
                    <Trash2 className="w-3 h-3" /> Clear
                  </button>
                )}
              </div>
            </div>
            <textarea
              data-testid="conv-bulk-ai-textarea"
              rows="5"
              value={transcript + (interimText ? ` ${interimText}` : '')}
              onChange={(e) => { setTranscript(e.target.value); setInterimText(''); }}
              placeholder="e.g. Talked to John Smith yesterday — interested in becoming a prayer partner. His email is john@example.com. Also called Mary Jones today, she wants to pledge 500 NOK monthly. Follow up next Tuesday."
              className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm focus:ring-2 focus:ring-purple-500"
            />
            {listening && (
              <div className="mt-2 flex items-center gap-2 text-xs text-red-600 font-medium">
                <Volume2 className="w-3.5 h-3.5 animate-pulse" />
                Listening… speak naturally, click Stop when done.
              </div>
            )}
            {!voiceSupported && (
              <p className="mt-1 text-xs text-slate-500 italic">
                Voice input works in Chrome/Edge. You can still type or paste notes here.
              </p>
            )}
          </div>

          {/* Parse button */}
          <button
            data-testid="conv-bulk-ai-parse"
            onClick={parseTranscript}
            disabled={parsing || !transcript.trim()}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-full font-semibold shadow hover:shadow-lg disabled:opacity-50 transition-all"
          >
            {parsing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {parsing ? 'AI is parsing…' : 'Parse with AI'}
          </button>

          {/* Parsed entries preview */}
          {entries.length > 0 && (
            <div data-testid="conv-bulk-ai-preview" className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-bold text-slate-800">Review {entries.length} entries</h4>
                <button onClick={saveAll} disabled={saving}
                        data-testid="conv-bulk-ai-save"
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white rounded-full text-xs font-semibold hover:bg-green-700 disabled:opacity-50">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  Save all
                </button>
              </div>
              <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                {entries.map((e, idx) => (
                  <div key={e._key || idx}
                       data-testid={`conv-bulk-ai-entry-${idx}`}
                       className={`border rounded-lg p-3 transition-all ${
                         e._enabled === false ? 'opacity-50 bg-slate-50 border-slate-200' : 'border-slate-200'
                       }`}>
                    <div className="flex items-center gap-2 mb-2">
                      <input type="checkbox"
                             checked={e._enabled !== false}
                             onChange={(ev) => updateEntry(idx, { _enabled: ev.target.checked })}
                             className="w-4 h-4 text-purple-600 rounded" />
                      <input
                        type="text"
                        value={e.name || ''}
                        onChange={(ev) => updateEntry(idx, { name: ev.target.value })}
                        className="flex-1 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-semibold"
                        placeholder="Name"
                      />
                      <button onClick={() => removeEntry(idx)} className="p-1.5 text-slate-400 hover:text-red-600">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <select value={e.status || 'initial_contact'}
                              onChange={(ev) => updateEntry(idx, { status: ev.target.value })}
                              className="px-2 py-1.5 rounded-lg border border-slate-200 bg-white text-xs">
                        {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      <select value={e.expected_pledge_type || ''}
                              onChange={(ev) => updateEntry(idx, { expected_pledge_type: ev.target.value })}
                              className="px-2 py-1.5 rounded-lg border border-slate-200 bg-white text-xs">
                        {PLEDGE_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      <input type="number" min="0" step="0.01" placeholder="NOK"
                             value={e.expected_amount || ''}
                             onChange={(ev) => updateEntry(idx, { expected_amount: parseFloat(ev.target.value) || 0 })}
                             className="px-2 py-1.5 rounded-lg border border-slate-200 bg-white text-xs" />
                      <input type="date" placeholder="Follow-up"
                             value={e.next_followup_at || ''}
                             onChange={(ev) => updateEntry(idx, { next_followup_at: ev.target.value })}
                             className="px-2 py-1.5 rounded-lg border border-slate-200 bg-white text-xs" />
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <input type="email" placeholder="Email"
                             value={e.email || ''}
                             onChange={(ev) => updateEntry(idx, { email: ev.target.value })}
                             className="px-2 py-1.5 rounded-lg border border-slate-200 bg-white text-xs" />
                      <input type="tel" placeholder="Phone"
                             value={e.phone || ''}
                             onChange={(ev) => updateEntry(idx, { phone: ev.target.value })}
                             className="px-2 py-1.5 rounded-lg border border-slate-200 bg-white text-xs" />
                    </div>
                    {e.notes && (
                      <textarea
                        rows="2"
                        value={e.notes}
                        onChange={(ev) => updateEntry(idx, { notes: ev.target.value })}
                        className="mt-2 w-full px-2 py-1.5 rounded-lg border border-slate-200 bg-slate-50 text-xs italic"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
